import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Loader2,
  Calendar,
  MapPin,
  Clock,
  Users,
  ExternalLink,
  Smartphone,
  Ticket,
  ArrowLeft,
  Share2,
  Copy,
  Check,
  Repeat,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { getEventById, getEventByShareCode, getEventShareCode } from "@/services/api";

const CRM_ENABLED = import.meta.env.VITE_CRM_ENABLED === "true";

// Format recurrence rule to human-readable text (matching mobile app)
const formatRecurrenceRule = (recurrence) => {
  if (!recurrence || recurrence.type === "never") return null;

  const { type, frequency, interval = 1, byDay, bySetPos, byMonthDay, byMonth, count, until } = recurrence;

  const dayMap = {
    SU: "Sunday", MO: "Monday", TU: "Tuesday", WE: "Wednesday",
    TH: "Thursday", FR: "Friday", SA: "Saturday"
  };

  const ordinalMap = {
    1: "First", 2: "Second", 3: "Third", 4: "Fourth", 5: "Fifth",
    "-1": "Last", "-2": "Second to last"
  };

  const monthMap = {
    1: "January", 2: "February", 3: "March", 4: "April", 5: "May", 6: "June",
    7: "July", 8: "August", 9: "September", 10: "October", 11: "November", 12: "December"
  };

  let baseText = "";

  // Handle weekly patterns
  if (type === "weekly" || frequency === "WEEKLY") {
    const days = byDay && byDay.length > 0 ? byDay.map(d => dayMap[d] || d).join(", ") : "";
    if (interval === 1) {
      baseText = days ? `Weekly on ${days}` : "Weekly";
    } else if (interval === 2) {
      baseText = days ? `Biweekly on ${days}` : "Biweekly";
    } else {
      baseText = days ? `Every ${interval} weeks on ${days}` : `Every ${interval} weeks`;
    }
  }
  // Handle daily patterns
  else if (type === "daily" || frequency === "DAILY") {
    baseText = interval === 1 ? "Daily" : `Every ${interval} days`;
  }
  // Handle monthly patterns
  else if (type === "monthly" || frequency === "MONTHLY") {
    if (bySetPos && bySetPos.length > 0 && byDay && byDay.length > 0) {
      const positions = bySetPos.map((pos, idx) => {
        const day = dayMap[byDay[idx]] || dayMap[byDay[0]];
        const ordinal = ordinalMap[pos] || `${pos}th`;
        return `${ordinal} ${day}`;
      }).join(" and ");
      baseText = interval === 1 ? `Monthly on the ${positions}` : `Every ${interval} months on the ${positions}`;
    } else if (byMonthDay && byMonthDay.length > 0) {
      const days = byMonthDay.map(d => d < 0 ? `${Math.abs(d)} days before month end` : `day ${d}`).join(", ");
      baseText = interval === 1 ? `Monthly on ${days}` : `Every ${interval} months on ${days}`;
    } else {
      baseText = interval === 1 ? "Monthly" : `Every ${interval} months`;
    }
  }
  // Handle yearly patterns
  else if (type === "yearly" || frequency === "YEARLY") {
    if (byMonth && byMonth.length > 0) {
      const months = byMonth.map(m => monthMap[m] || m).join(", ");
      baseText = interval === 1 ? `Yearly in ${months}` : `Every ${interval} years in ${months}`;
    } else {
      baseText = interval === 1 ? "Yearly" : `Every ${interval} years`;
    }
  } else {
    baseText = "Recurring";
  }

  // Add count or until suffix
  if (count) {
    baseText += `, ${count} times`;
  } else if (until) {
    const untilDate = new Date(until.seconds ? until.seconds * 1000 : until._seconds ? until._seconds * 1000 : until);
    if (!isNaN(untilDate.getTime())) {
      baseText += ` until ${untilDate.toLocaleDateString()}`;
    }
  }

  return baseText;
};

export default function EventDetail() {
  const { eventId, shareCode: shareCodeParam } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine if this is a share link view
  const isShareView = !!shareCodeParam;

  // Share state
  const [shareCode, setShareCode] = useState(shareCodeParam || null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch by share code or event ID
        let eventData;
        if (shareCodeParam) {
          eventData = await getEventByShareCode(shareCodeParam);
        } else {
          eventData = await getEventById(eventId);
          // Store share code if available
          if (eventData.shareCode) {
            setShareCode(eventData.shareCode);
          }
        }
        
        setEvent(eventData);
      } catch (err) {
        console.error("Error fetching event:", err);
        setError(err.response?.data?.error || "Event not found");
      } finally {
        setLoading(false);
      }
    };

    if (eventId || shareCodeParam) {
      fetchEvent();
    }
  }, [eventId, shareCodeParam]);

  // Try to open app via deep link
  const openInApp = () => {
    const code = shareCodeParam || shareCode || eventId;
    const appUrl = `muslim://e/${code}`;
    window.location.href = appUrl;
  };

  // Get share link
  const handleShare = async () => {
    try {
      if (!shareCode) {
        const result = await getEventShareCode(event.id);
        setShareCode(result.shareCode);
      }
      setShowShareDialog(true);
    } catch (err) {
      console.error("Share error:", err);
    }
  };

  // Copy share link
  const copyShareLink = () => {
    const url = `${window.location.origin}/e/${shareCode}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Format date for display
  const formatEventDate = (dateValue) => {
    if (!dateValue) return null;

    let date;
    if (dateValue.seconds) {
      date = new Date(dateValue.seconds * 1000);
    } else if (dateValue._seconds) {
      date = new Date(dateValue._seconds * 1000);
    } else {
      date = new Date(dateValue);
    }

    if (isNaN(date.getTime())) return null;

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Add to calendar
  const addToCalendar = () => {
    if (!event) return;

    let eventDate;
    if (event.date?.seconds) {
      eventDate = new Date(event.date.seconds * 1000);
    } else if (event.date?._seconds) {
      eventDate = new Date(event.date._seconds * 1000);
    } else {
      eventDate = new Date(event.date);
    }

    let endDate = new Date(eventDate.getTime() + 60 * 60 * 1000);
    if (event.endTime) {
      const match = event.endTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        if (match[3]?.toUpperCase() === "PM" && hours !== 12) hours += 12;
        if (match[3]?.toUpperCase() === "AM" && hours === 12) hours = 0;
        endDate = new Date(eventDate);
        endDate.setHours(hours, minutes, 0, 0);
      }
    }

    const formatDate = (d) =>
      d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

    const googleUrl =
      `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(event.title)}` +
      `&dates=${formatDate(eventDate)}/${formatDate(endDate)}` +
      `&details=${encodeURIComponent(event.description || "")}` +
      `&location=${encodeURIComponent(event.location || "")}`;

    window.open(googleUrl, "_blank");
  };

  // Get directions
  const getDirections = () => {
    if (!event?.location) return;
    const query = encodeURIComponent(event.location);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
  };

  if (loading) {
    return (
      <div className={isShareView ? "flex min-h-screen items-center justify-center bg-background" : "flex min-h-[400px] items-center justify-center"}>
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading event...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={isShareView ? "flex min-h-screen items-center justify-center bg-background p-4" : "mx-auto max-w-2xl p-4"}>
        <Card className={isShareView ? "max-w-md" : ""}>
          <CardHeader>
            <CardTitle className="text-destructive">Event Not Found</CardTitle>
            <CardDescription>
              {isShareView 
                ? "This event link may be expired or invalid." 
                : "This event may have been removed or you don't have access."
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button 
              onClick={() => isShareView ? navigate("/") : navigate(-1)} 
              variant="outline"
              className={isShareView ? "w-full" : ""}
            >
              {!isShareView && <ArrowLeft className="mr-2 h-4 w-4" />}
              {isShareView ? "Go to Home" : "Go Back"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={isShareView ? "min-h-screen bg-background" : "mx-auto max-w-3xl p-4"}>
      {/* Share View: Header with Logo */}
      {isShareView && (
        <div className="border-b bg-card px-4 py-3">
          <div className="mx-auto flex max-w-2xl items-center justify-between">
            <Link 
              to="/" 
              className="text-xl font-bold bg-gradient-to-r from-[#bf9a2f] via-[#e6c86e] to-[#bf9a2f] bg-clip-text text-transparent transition-opacity hover:opacity-80"
            >
              Muslim+
            </Link>
            <Button onClick={openInApp} size="sm" className="gap-2">
              <Smartphone className="h-4 w-4" />
              Open in App
            </Button>
          </div>
        </div>
      )}

      {/* Regular View: Back Button */}
      {!isShareView && (
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      )}

      <div className={isShareView ? "mx-auto max-w-2xl p-4" : ""}>
        <Card>
        {/* Event Flyer */}
        {event.flyerUrl && (
          <div className="relative aspect-video w-full overflow-hidden rounded-t-lg">
            <img
              src={event.flyerUrl}
              alt={event.title}
              className="h-full w-full object-cover"
            />
          </div>
        )}

        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="mb-2 flex flex-wrap gap-2">
                {event.eventMode === "registration_required" && (
                  <Badge variant="secondary" className="gap-1">
                    <Ticket className="h-3 w-3" />
                    Registration Required
                  </Badge>
                )}
                {formatRecurrenceRule(event.recurrence) && (
                  <Badge variant="outline" className="gap-1">
                    <Repeat className="h-3 w-3" />
                    Recurring
                  </Badge>
                )}
              </div>
              <CardTitle className="text-2xl">{event.title}</CardTitle>
              {(event.masjid || event.organization) && (
                <CardDescription className="text-base">
                  {event.masjidNickname || event.masjid || event.organization}
                </CardDescription>
              )}
            </div>
            {/* Only show share button on regular event detail view */}
            {!isShareView && (
              <Button variant="outline" size="icon" onClick={handleShare}>
                <Share2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Date & Time */}
          <div className="flex items-start gap-3">
            <Calendar className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-medium">{formatEventDate(event.date)}</p>
              {event.time && (
                <p className="text-muted-foreground">
                  {event.time}
                  {event.endTime ? ` - ${event.endTime}` : ""}
                </p>
              )}
            </div>
          </div>

          {/* Recurrence */}
          {formatRecurrenceRule(event.recurrence) && (
            <div className="flex items-start gap-3">
              <Repeat className="mt-0.5 h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Repeats</p>
                <p className="text-muted-foreground">
                  {formatRecurrenceRule(event.recurrence)}
                </p>
              </div>
            </div>
          )}

          {/* Location */}
          {event.location && (
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">{event.location}</p>
                {event.sublocation && (
                  <p className="text-muted-foreground">{event.sublocation}</p>
                )}
                <Button
                  variant="link"
                  className="h-auto p-0 text-primary"
                  onClick={getDirections}
                >
                  Get directions <ExternalLink className="ml-1 h-3 w-3" />
                </Button>
              </div>
            </div>
          )}

          {/* Registration Stats */}
          {event.eventMode === "registration_required" && event.registrationStats && (
            <div className="flex items-start gap-3">
              <Users className="mt-0.5 h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">
                  {event.registrationStats.approvedCount} registered
                </p>
                {event.registrationStats.availableSpots !== null && (
                  <p className="text-muted-foreground">
                    {event.registrationStats.availableSpots} spots remaining
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="border-t pt-4">
              <h3 className="mb-2 font-medium">About this event</h3>
              <p className="whitespace-pre-wrap text-muted-foreground">
                {event.description}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row">
            <Button
              onClick={addToCalendar}
              variant="outline"
              className="flex-1 gap-2"
            >
              <Calendar className="h-4 w-4" />
              Add to Calendar
            </Button>
            {event.location && (
              <Button
                onClick={getDirections}
                variant="outline"
                className="flex-1 gap-2"
              >
                <MapPin className="h-4 w-4" />
                Directions
              </Button>
            )}
            {/* Only show share button on regular event detail view */}
            {!isShareView && (
              <Button
                onClick={handleShare}
                variant="outline"
                className="flex-1 gap-2"
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
            )}
          </div>

          {/* Get the App - RSVP/Register + Download Links Combined */}
          <div className="rounded-lg border-2 border-primary/20 bg-primary/5 p-4">
            <div className="mb-3 flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              <span className="font-semibold">
                {CRM_ENABLED ? (
                  event.eventMode === "registration_required" 
                    ? "Register in the Muslim+ App" 
                    : "RSVP in the Muslim+ App"
                ) : (
                  "Download the Muslim+ app for more features"
                )}
              </span>
            </div>
            <p className="mb-4 text-sm text-muted-foreground">
              Download the Muslim+ app to {CRM_ENABLED ? (event.eventMode === "registration_required" ? "register for" : "RSVP to") : "view more details about"} this event, 
              get reminders, and access more features.
            </p>
            <Button onClick={openInApp} className="mb-4 w-full gap-2">
              <Smartphone className="h-4 w-4" />
              Open in App
            </Button>
            <div className="flex items-center justify-center gap-4 border-t pt-4">
              <a
                href="https://apps.apple.com/us/app/muslim-prayer-companion/id6745054019"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                App Store <ExternalLink className="h-3 w-3" />
              </a>
              <span className="text-muted-foreground">|</span>
              <a
                href="https://play.google.com/store/apps/details?id=com.psu.pdxmsa&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                Google Play <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>

      {/* Share Dialog - Only on regular event detail view */}
      {!isShareView && (
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share Event</DialogTitle>
            <DialogDescription>
              Share this event with friends and family.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {shareCode ? (
              <div className="space-y-2">
                <Label>Share Link</Label>
                <div className="flex gap-2">
                  <Input
                    value={`${window.location.origin}/e/${shareCode}`}
                    readOnly
                  />
                  <Button onClick={copyShareLink} variant="outline">
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {copied && (
                  <p className="text-sm text-green-600">Copied to clipboard!</p>
                )}
              </div>
            ) : (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowShareDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}
